import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Search, Paperclip, X, Send, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  abierto:     { bg: '#fee2e2', color: '#991b1b', label: 'Abierto' },
  en_proceso:  { bg: '#fef3c7', color: '#92400e', label: 'En proceso' },
  resuelto:    { bg: '#dcfce7', color: '#166534', label: 'Resuelto' },
};

export default function SupportTicketsTab({ token }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await axios.get(`${API}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setTickets(data.tickets || []);
    } catch {
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = tickets.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.ticket_number || '').toLowerCase().includes(q) ||
      (t.name || '').toLowerCase().includes(q) ||
      (t.email || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  });

  const counts = tickets.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  const saveTicket = async (patch) => {
    if (!selected) return;
    setSavingId(selected.id);
    try {
      const { data } = await axios.put(`${API}/support/tickets/${selected.id}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelected(data);
      setTickets((prev) => prev.map((t) => (t.id === data.id ? data : t)));
      toast.success('Ticket actualizado');
    } catch {
      toast.error('No se pudo actualizar el ticket');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="support-tickets-tab">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={tickets.length} accent="#0f172a" />
        <StatCard label="Abiertos" value={counts.abierto || 0} accent="#991b1b" />
        <StatCard label="En proceso" value={counts.en_proceso || 0} accent="#92400e" />
        <StatCard label="Resueltos" value={counts.resuelto || 0} accent="#166534" />
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={16} className="text-slate-400" />
          <input
            data-testid="support-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por número, nombre, correo o descripción…"
            className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select
            data-testid="support-filter-status"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded text-sm outline-none"
          >
            <option value="">Todos los estados</option>
            <option value="abierto">Abiertos</option>
            <option value="en_proceso">En proceso</option>
            <option value="resuelto">Resueltos</option>
          </select>
        </div>
        <button
          onClick={load}
          data-testid="support-refresh"
          className="p-2 border border-slate-300 rounded hover:bg-slate-50"
          title="Recargar"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Cargando tickets…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500" data-testid="support-empty">No hay tickets registrados.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left">Número</th>
                <th className="px-4 py-2 text-left">Solicitante</th>
                <th className="px-4 py-2 text-left">Correo</th>
                <th className="px-4 py-2 text-left">Fecha</th>
                <th className="px-4 py-2 text-left">Estado</th>
                <th className="px-4 py-2 text-left">Adj.</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="support-ticket-list">
              {filtered.map((t) => {
                const s = STATUS_STYLES[t.status] || STATUS_STYLES.abierto;
                return (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono font-semibold text-slate-800">{t.ticket_number}</td>
                    <td className="px-4 py-2">{t.name}</td>
                    <td className="px-4 py-2 text-slate-600">{t.email}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">
                      {new Date(t.created_at).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-2">
                      <span style={{ background: s.bg, color: s.color, padding: '0.15rem 0.55rem', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600 }}>{s.label}</span>
                    </td>
                    <td className="px-4 py-2">{t.attachments?.length ? <Paperclip size={14} className="text-slate-500" /> : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`support-view-${t.ticket_number}`}
                        onClick={() => setSelected(t)}
                        className="text-blue-600 hover:underline text-sm font-medium"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <TicketDetailModal
          ticket={selected}
          onClose={() => setSelected(null)}
          onSave={saveTicket}
          saving={savingId === selected.id}
        />
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white rounded-lg border-l-4 shadow-sm px-4 py-3" style={{ borderColor: accent }}>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
  </div>
);

const TicketDetailModal = ({ ticket, onClose, onSave, saving }) => {
  const [status, setStatus] = useState(ticket.status);
  const [response, setResponse] = useState(ticket.response || '');
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setStatus(ticket.status);
    setResponse(ticket.response || '');
  }, [ticket.id, ticket.status, ticket.response]);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      data-testid="support-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 10, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Ticket</div>
            <h3 className="text-xl font-mono font-bold text-slate-900">{ticket.ticket_number}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoBox label="Nombre" value={ticket.name} />
            <InfoBox label="Teléfono" value={ticket.phone} />
            <InfoBox label="Correo" value={ticket.email} />
            <InfoBox label="Creado" value={new Date(ticket.created_at).toLocaleString('es-CO')} />
          </div>

          <div>
            <div className="text-xs text-slate-500 uppercase mb-1">Descripción</div>
            <div className="bg-slate-50 p-3 rounded text-sm text-slate-800 whitespace-pre-wrap">{ticket.description}</div>
          </div>

          {ticket.attachments?.length > 0 && (
            <div>
              <div className="text-xs text-slate-500 uppercase mb-2">Adjuntos</div>
              <div className="flex flex-wrap gap-2">
                {ticket.attachments.map((a) => (
                  <a
                    key={a.filename}
                    href={`${backend}${a.url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 px-3 py-1.5 rounded text-xs font-medium"
                  >
                    <Paperclip size={14} /> {a.original_name}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-500 uppercase block mb-1">Estado</label>
            <select
              data-testid="support-detail-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
            >
              <option value="abierto">Abierto</option>
              <option value="en_proceso">En proceso</option>
              <option value="resuelto">Resuelto</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-500 uppercase block mb-1">Respuesta / Nota interna</label>
            <textarea
              data-testid="support-detail-response"
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-sans"
              placeholder="Escribe la respuesta o una nota interna…"
            />
          </div>

          {ticket.responded_by && (
            <div className="text-xs text-slate-500">
              Última respuesta por <span className="font-medium">{ticket.responded_by}</span>
              {ticket.responded_at && (<> · {new Date(ticket.responded_at).toLocaleString('es-CO')}</>)}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 text-sm"
          >
            Cerrar
          </button>
          <button
            data-testid="support-detail-save"
            disabled={saving}
            onClick={() => onSave({ status, response })}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-60"
          >
            <Send size={14} /> {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoBox = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase">{label}</div>
    <div className="text-slate-800 font-medium">{value || '—'}</div>
  </div>
);
